import { FC, memo, useMemo } from 'react'

import { Button, Typography } from '@/components/ui'
import ButtonLink from '@/components/ui/button_link/ButtonLink'

import Download from '@/assets/icons/download.svg'
import Flag from '@/assets/icons/flag.svg'

import { API_URL } from '@/config/api.config'

import keycloak from '@/auth/keycloak'
import { useAuth } from '@/auth/useAuth'
import { IHighlightsFolder } from '@/types/highlight.interface'
import { IFile, ITaskSearchMatch, ITranscription } from '@/types/task.interface'

import { type TColoredHighlightRange } from '../helpers/getHighlightedText'
import { useTranscriptionErrorReport } from '../hooks/useTranscriptionErrorReport'

import TranscriptionChunk from './TranscriptionChunk'

const getExpandedKeywordRange = (
	match: ITaskSearchMatch,
	items: ITranscription[]
) => {
	const matchedChunks = items.filter(item => {
		if (match.row_ids?.length) {
			return match.row_ids.includes(item.id)
		}

		return item.start < match.end && item.end > match.start
	})

	const matchedSegments = matchedChunks
		.flatMap(item => item.segments)
		.filter(segment => {
			if (segment.start === segment.end) {
				return segment.start >= match.start && segment.start <= match.end
			}

			return segment.start < match.end && segment.end > match.start
		})

	if (!matchedSegments.length) {
		return {
			start: match.start,
			end: match.end
		}
	}

	return {
		start: Math.min(...matchedSegments.map(segment => segment.start)),
		end: Math.max(...matchedSegments.map(segment => segment.end))
	}
}

interface IProps {
	items: ITranscription[]
	highlightable?: boolean
	isCollapsed?: boolean
	refCollector?: (el: HTMLDivElement | null, index: number) => void
	onRendered?: () => void
	onSeek?: (sec: number) => void
	translation?: boolean
	folders?: IHighlightsFolder[]
	isTranscribingStopped?: boolean
	taskId?: string
	keywordMatches?: ITaskSearchMatch[]
}

const TranscriptionBlock: FC<IProps> = ({
	items,
	highlightable = false,
	isCollapsed,
	refCollector,
	onRendered,
	onSeek,
	translation,
	folders,
	isTranscribingStopped,
	taskId,
	keywordMatches
}) => {
	const { isInt, username, userEmail } = useAuth()

	const canReportTranscriptionError = highlightable && translation

	const {
		transcriptionTextRef,
		reportData,
		reportTranscriptionError
	} = useTranscriptionErrorReport({
		enabled: canReportTranscriptionError,
		taskId,
		username,
		userEmail
	})

	const visibleKeywordMatches = useMemo(() => {
		if (!keywordMatches?.length) return []

		return keywordMatches.filter(match => {
			if (!match.kind) return true

			return translation
				? match.kind === 'translation'
				: match.kind === 'transcription'
		})
	}, [keywordMatches, translation])

	const coloredHighlights = useMemo<TColoredHighlightRange[]>(() => {
		const folderHighlights =
			folders?.flatMap(folder => {
				return folder.segments.map((segment, index) => ({
					id: `${folder.id}-${segment.id || index}`,
					start: segment.start,
					end: segment.end,
					color: folder.color_code || '#B3B3B3',
					type: 'segment' as const,
					priority: 1
				}))
			}) || []

		const keywordHighlights = visibleKeywordMatches.map((match, index) => {
			const range = getExpandedKeywordRange(match, items)

			return {
				id: `keyword-${match.kind}-${index}-${range.start}-${range.end}`,
				start: range.start,
				end: range.end,
				color: '#175BCC',
				type: 'keyword' as const,
				priority: 2
			}
		})

		return [...folderHighlights, ...keywordHighlights].sort((a, b) => {
			if (a.start !== b.start) return a.start - b.start
			return a.end - b.end
		})
	}, [folders, items, visibleKeywordMatches])

	const downloadFiles = useMemo<IFile[]>(() => {
		if (!taskId || !keycloak.token) return []

		const videoParams = new URLSearchParams({
			token: keycloak.token
		})

		const translationParams = new URLSearchParams({
			language: 'en',
			token: keycloak.token
		})

		return [
			{
				name: 'TXT',
				source: `${API_URL}/tasks/${taskId}/translation/download?${translationParams.toString()}`,
				filename: `translation-${taskId}-en.txt`
			},
			{
				name: 'MP4',
				source: `${API_URL}/tasks/${taskId}/video/download?${videoParams.toString()}`,
				filename: `live-${taskId}.mp4`
			}
		]
	}, [taskId])

	const resolveDownloadFile = async (
		type: string
	): Promise<IFile | undefined> => {
		if (!taskId) return undefined

		await keycloak.updateToken(30)

		const token = keycloak.token

		if (!token) {
			throw new Error('Access token is unavailable')
		}

		if (type === 'TXT') {
			const params = new URLSearchParams({
				language: 'en',
				token
			})

			return {
				name: 'TXT',
				source: `${API_URL}/tasks/${taskId}/translation/download?${params.toString()}`,
				filename: `translation-${taskId}-en.txt`
			}
		}

		if (type === 'MP4') {
			const params = new URLSearchParams({ token })

			return {
				name: 'MP4',
				source: `${API_URL}/tasks/${taskId}/video/download?${params.toString()}`,
				filename: `live-${taskId}.mp4`
			}
		}

		return undefined
	}

	return (
		<div className='relative'>
			{/* Transcription/translation chunks */}
			<div
				ref={canReportTranscriptionError ? transcriptionTextRef : undefined}
			>
				{items.map((item, idx) => (
					<TranscriptionChunk
						key={`${item.id}-${item.start}-${item.end}-${idx}`}
						item={item}
						index={idx}
						isCollapsed={isCollapsed}
						highlightable={highlightable}
						coloredHighlights={coloredHighlights}
						refCollector={refCollector}
						onRendered={onRendered}
						isLast={idx === items.length - 1}
						onSeek={onSeek}
						translation={translation}
					/>
				))}
			</div>

			{!isInt && translation && isTranscribingStopped && (
				<Typography
					variant='text'
					className='w-3/4 !font-[500] !text-[13px] !text-left !leading-[16px] text-red-475'
				>
					Transcribing stopped
				</Typography>
			)}

			{/* Downloading buttons block */}
			{translation && (
				<div className='fixed bottom-[17px] right-[24px] flex items-center gap-2'>
					{reportData && (
						<Button
							data-transcription-report-button
							onClick={reportTranscriptionError}
							className='rounded-[4px] !py-[5px] !px-2 bg-red-500 hover:bg-red-400 cursor-pointer'
							classNameText='flex items-center gap-2 !text-[12px] text-white'
						>
							<Flag className='w-3 h-3' />
							Translation error
						</Button>
					)}
					<ButtonLink
						type='TXT'
						label={
							<div className='flex items-center gap-2 text-black'>
								<Download className='w-3 h-3' />
								<span className='!font-[500] !text-[12px]'>Translation</span>
							</div>
						}
						files={downloadFiles}
						resolveFile={resolveDownloadFile}
						linkClassName='rounded-[4px] !py-[6px] !px-2 bg-white hover:bg-neutral-100 cursor-pointer'
					/>
					<ButtonLink
						type='MP4'
						label={
							<div className='flex items-center gap-2 text-black'>
								<Download className='w-3 h-3' />
								<span className='!font-[500] !text-[12px]'>Full live</span>
							</div>
						}
						files={downloadFiles}
						resolveFile={resolveDownloadFile}
						linkClassName='rounded-[4px] !py-[6px] !px-2 bg-white hover:bg-neutral-100 cursor-pointer'
					/>
				</div>
			)}
		</div>
	)
}

export default memo(TranscriptionBlock)
